import React, { createContext, useContext, useState, useEffect } from 'react';
import { authApi } from '../utils/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('accessToken');
      if (token) {
        try {
          const response = await authApi.me();
          setUser(response.data.user);
          setWorkspace(response.data.workspace);
          setWorkspaces(response.data.workspaces || []);
        } catch (error) {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const applyAuthPayload = (payload) => {
    setUser(payload.user);
    setWorkspace(payload.workspace);
    setWorkspaces(payload.workspaces || []);
  };

  const login = async (email, password) => {
    const response = await authApi.login({ email, password });
    const { accessToken, refreshToken } = response.data;
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    applyAuthPayload(response.data);
    // /login response doesn't include the full memberships array; fetch it.
    const me = await authApi.me();
    setWorkspaces(me.data.workspaces || []);
    return response.data;
  };

  const register = async (data) => {
    const response = await authApi.register(data);
    const { accessToken, refreshToken } = response.data;
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    applyAuthPayload(response.data);
    setWorkspaces([
      {
        id: response.data.workspace.id,
        name: response.data.workspace.name,
        slug: response.data.workspace.slug,
        role: response.data.user.role,
      },
    ]);
    return response.data;
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      // Ignore logout errors
    }
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setUser(null);
    setWorkspace(null);
    setWorkspaces([]);
  };

  const refreshMe = async () => {
    const response = await authApi.me();
    setUser(response.data.user);
    setWorkspace(response.data.workspace);
    setWorkspaces(response.data.workspaces || []);
    return response.data;
  };

  const value = {
    user,
    workspace,
    workspaces,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    refreshMe,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
